import React, { FC, MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button, Dropdown, Modal, ModalSize, formatNumber } from '@column/column-ui-kit';

import { Datepicker, NotificationList } from '~/components';
import { DatePreview } from '~/elements/DatePreview';
import { useLazyBankAccounts } from '~/hooks';
import { useCustomizedBankAccountStatement } from '~/hooks/useReporting';
import { BankAccount } from '~/repositories/BankAccountRepository';
import { useNotificationStore } from '~/stores/Notification';
import { FormElement, FormLabel, Grid, Inner } from '~/styles';
import { ReportingServiceGetCustomizedBankAccountStatementParams } from '~/typings/API';
import { downloadBlob, getPrettyFilename } from '~/util/downloadFile';

export interface ModalCreateStatementProps {
  isOpen?: boolean;
  onClose?: () => void;
  bankAccount?: BankAccount;
  dates?: string[];
}

const getFilenameFromStatementRequest = (
  req: ReportingServiceGetCustomizedBankAccountStatementParams | undefined,
  bankAccount: BankAccount | undefined
): string => {
  const fileDescription = [] as string[];
  if (bankAccount) {
    if (bankAccount.displayName) {
      fileDescription.push(bankAccount.displayName);
    } else if (bankAccount.description) {
      fileDescription.push(bankAccount.description);
    }
  }

  if (req) {
    const { fromDate, toDate } = req;
    fileDescription.push(`${fromDate} to ${toDate}`);
  }

  return getPrettyFilename({
    fileType: 'Custom Statement',
    fileDescription,
    ext: 'csv',
  });
};

export const ModalCreateStatement: FC<ModalCreateStatementProps> = ({
  isOpen,
  onClose,
  bankAccount: initialBankAccount,
  dates: initialDates,
}) => {
  const { addDangerNotification, addSuccessNotification } = useNotificationStore();

  const activeRequest = useRef<ReportingServiceGetCustomizedBankAccountStatementParams | undefined>();
  const [bankAccount, setBankAccount] = useState<BankAccount | undefined>(initialBankAccount);
  const { response: bankAccounts, setQueryParams: searchBankAccounts } = useLazyBankAccounts();
  const [dates, setDates] = useState<string[]>(initialDates ?? []);
  const [hoverDate, setHoverDate] = useState<string | undefined>(undefined);
  const [displayDate, setDisplayDate] = useState<string | undefined>(undefined);
  const prevIsOpenRef = useRef<boolean | undefined>(undefined);
  const [isCreateStatementLoading, setIsCreateStatementLoading] = useState<boolean>(false);
  const [isDownloadAnimationRunning, setIsDownloadAnimationRunning] = useState<boolean>(false);
  const [isBankAccountDropdownVisible, setIsBankAccountDropdownVisible] = useState<boolean>(!initialBankAccount);

  useEffect(() => {
    const wasClosed = prevIsOpenRef.current === false;
    const isNowOpen = isOpen === true;
    const isInitialOpen = prevIsOpenRef.current === undefined && isOpen === true;
    prevIsOpenRef.current = isOpen;

    if (isOpen) {
      setBankAccount(initialBankAccount);
      setDates(initialDates ?? []);
      setIsBankAccountDropdownVisible(!initialBankAccount);
      if (!initialBankAccount) {
        searchBankAccounts({});
      }

      if ((wasClosed && isNowOpen) || isInitialOpen) {
        if (initialDates && initialDates.length > 0) {
          setDisplayDate(initialDates[0]);
        } else {
          setDisplayDate(undefined);
        }
      }
    }
  }, [isOpen, initialBankAccount, initialDates]);

  const dropdownOptions = useMemo(() => {
    const selectedBankAccount = bankAccount ? [bankAccount] : [];
    const otherBankAccounts = bankAccounts?.bankAccounts.filter((acct) => acct.id !== bankAccount?.id) ?? [];
    const options = [...selectedBankAccount, ...otherBankAccounts];
    return (
      options.map((acct) => ({
        label: `${acct.displayName || acct.description}`,
        suffix: `${formatNumber(acct.balances.availableAmount)}`,
        small: acct.id,
        value: acct.id,
      })) ?? []
    );
  }, [bankAccount, bankAccounts]);

  const earliestSelectableDate = useMemo(() => {
    if (!bankAccount) {
      return new Date('2022-01-1');
    }
    const accountCreatedAt = new Date(bankAccount.createdAt);
    accountCreatedAt.setDate(accountCreatedAt.getDate() - 1);
    return accountCreatedAt;
  }, [bankAccount]);

  const customizedBankAccountStatement = useCustomizedBankAccountStatement({
    onSuccess: (data) => {
      const filename = getFilenameFromStatementRequest(activeRequest.current, bankAccount);
      downloadBlob(data, filename);
      addSuccessNotification({
        content: 'Statement created successfully',
      });
      activeRequest.current = undefined;
      setIsCreateStatementLoading(false);
      onClose?.();
    },
    onError: (error) => {
      addDangerNotification({
        content: error.message,
        display: 'popup',
      });
      activeRequest.current = undefined;
      setIsCreateStatementLoading(false);
    },
  });

  const createStatement = useCallback(() => {
    if (!bankAccount) {
      addDangerNotification({
        content: 'Please select an account.',
        display: 'popup',
      });

      return;
    }

    if (dates.length !== 2) {
      addDangerNotification({
        content: 'Please select a date range.',
        display: 'popup',
      });

      return;
    }

    const [fromDate, toDate] = dates.toSorted();

    const request: ReportingServiceGetCustomizedBankAccountStatementParams = {
      bankAccountId: bankAccount.id,
      fromDate,
      toDate,
      type: 'csv',
    };
    activeRequest.current = request;
    setIsDownloadAnimationRunning(true);
    customizedBankAccountStatement.createRequest(request);
  }, [bankAccount, dates]);

  const handleSubmit = useCallback(
    (event: MouseEvent) => {
      event.preventDefault();
      createStatement();
    },
    [createStatement]
  );

  const title = !isBankAccountDropdownVisible ? (bankAccount?.displayName ?? bankAccount?.description) : undefined;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.Medium}>
      <Modal.AnimatedIconCircle
        icon="AnimationDownload"
        running={isDownloadAnimationRunning}
        onDone={() => setIsDownloadAnimationRunning(false)}
      />

      <Modal.Content beforeContent={<NotificationList display="popup" />}>
        <Modal.Headline fullWidth>Custom CSV Statement</Modal.Headline>
        <Inner py={0}>
          <Modal.Paragraph align="center">{`Create a statement ${title ? `for ${title}` : ''} for a date range or a single date. The statement will be downloaded as a .csv file.`}</Modal.Paragraph>
        </Inner>
        <Inner>
          <Grid vertical>
            {isBankAccountDropdownVisible && (
              <FormElement fullWidth>
                <Inner pb={6} pt={0} px={0}>
                  <FormLabel style={{ textAlign: 'left' }}>Account</FormLabel>
                </Inner>
                <Dropdown
                  options={dropdownOptions}
                  active={bankAccount?.id}
                  fullWidth
                  search
                  searchLabel="Search for description"
                  onSearchChange={(description: string) => searchBankAccounts({ description })}
                  onChange={(bankAccountId: string) => {
                    setBankAccount(bankAccounts?.bankAccounts.find((acct) => acct.id === bankAccountId));
                  }}
                />
              </FormElement>
            )}
            <FormElement fullWidth>
              <Inner pb={12} pt={0} px={0}>
                <FormLabel style={{ textAlign: 'left' }}>
                  Date Range <DatePreview hoverDate={hoverDate} selectedDates={dates} />
                </FormLabel>
              </Inner>
              <Datepicker
                date={dates}
                showDate={displayDate}
                onDateSubmit={(value) => {
                  setDates(value as string[]);
                }}
                showMonths={2}
                disableBefore={earliestSelectableDate}
                disableAfter={new Date()}
                onHoverDate={(value) => setHoverDate(value)}
              />
            </FormElement>
          </Grid>
        </Inner>
      </Modal.Content>

      <Modal.Footer $withBackground>
        <Button variant="secondary" onClick={onClose} fullWidth>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          isDisabled={isCreateStatementLoading || !bankAccount || dates.length !== 2}
          isLoading={isCreateStatementLoading}
          fullWidth
        >
          Create Statement
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
